import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ClockIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';
import EndpointRunner from '../components/runner/EndpointRunner';
import config from '../config';

interface ExecutionHistoryItem {
  id: string;
  type: 'sync' | 'async';
  success: boolean;
  timestamp: string;
  requestId?: string;
  endpointId?: string;
}

export default function RunnerPage() {
  const [executionHistory, setExecutionHistory] = useState<ExecutionHistoryItem[]>([]);
  const [searchParams] = useSearchParams();
  // Deep-link support (e.g. Composer's "Try it" button): /runner?endpointId=...
  const initialEndpointId = searchParams.get('endpointId') || undefined;

  const handleExecutionComplete = (result: {
    type: 'sync' | 'async';
    success: boolean;
    timestamp: string;
    requestId?: string;
    endpointId?: string;
  }) => {
    const historyItem: ExecutionHistoryItem = {
      id: crypto.randomUUID(),
      type: result.type,
      success: result.success,
      timestamp: result.timestamp,
      requestId: result.requestId,
      endpointId: result.endpointId,
    };
    setExecutionHistory(prev => [historyItem, ...prev].slice(0, 10)); // Keep last 10
  };

  return (
    <div>
      <PageHeader
        title="Runner"
        description="Run endpoints and monitor AI agent collaborations"
        actions={
          <a
            // The service's real OpenAPI document. This used to point at the
            // app-relative "/api", which never resolved — the API lives on the
            // backend origin, read from config (task 385).
            href={`${config.API_URL}/openapi.json`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
            API Docs
          </a>
        }
      />

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Runner Area */}
        <div className="lg:col-span-2">
          <EndpointRunner onExecutionComplete={handleExecutionComplete} initialEndpointId={initialEndpointId} />
        </div>

        {/* Execution History Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <ClockIcon className="h-5 w-5 mr-2 text-gray-400" />
              Recent Executions
            </h3>

            {executionHistory.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No runs yet. Select an endpoint and click Run to get started.
              </p>
            ) : (
              <div className="space-y-3">
                {executionHistory.map((item) => (
                  <div
                    key={item.id}
                    className={`p-3 rounded-lg border ${
                      item.success
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                        : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className={`w-2 h-2 rounded-full ${
                          item.success ? 'bg-green-500' : 'bg-red-500'
                        }`} />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {item.success ? 'Success' : 'Failed'}
                        </span>
                        {item.type === 'async' && (
                          <span className="px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                            Async
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(item.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    {item.requestId && (
                      <div className="mt-2">
                        <Link
                          to={`/collaborations?requestId=${item.requestId}`}
                          className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                          View Collaboration
                        </Link>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Quick Links
            </h3>
            <div className="space-y-2">
              <Link
                to="/endpoints"
                className="block text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                Manage Endpoints
              </Link>
              <Link
                to="/configs"
                className="block text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                Collaboration Configs
              </Link>
              <Link
                to="/collaborations"
                className="block text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                View All Collaborations
              </Link>
              <Link
                to="/agents"
                className="block text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                Manage Agents
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
